import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { TID } from "../../lib/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = [
  "Founder / CEO",
  "CTO / VP Engineering",
  "Head of Product",
  "Product Manager",
  "Engineering Lead",
  "Other",
];
const NEEDS = [
  "AI Product Engineering",
  "AI Agents & RAG",
  "SaaS Product Development",
  "Platform Modernization",
  "Dedicated Product Team",
  "Workflow Automation",
  "Software Agency Partnership",
  "Other",
];
const STAGES = [
  "Exploring",
  "Planning",
  "Prototype Exists",
  "Product Already Live",
  "Modernizing Existing Platform",
  "Scaling Engineering Capacity",
];
const BUDGETS = [
  "Under $10K",
  "$10K – $25K",
  "$25K – $50K",
  "$50K – $100K",
  "$100K+ Ongoing Engineering Partnership",
];

const emptyForm = {
  full_name: "",
  work_email: "",
  company: "",
  website: "",
  role: "",
  need: "",
  project_stage: "",
  budget: "",
  main_challenge: "",
};

const inputCls =
  "w-full bg-transparent border-b border-white/15 focus:border-[#FF5F1F] outline-none py-3 text-white placeholder:text-zinc-600 text-sm md:text-base transition-colors";
const labelCls =
  "font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2 block";

const Field = ({ label, children, required = false }) => (
  <div className="flex flex-col">
    <label className={labelCls}>
      {label} {required && <span className="text-[#FF5F1F]">*</span>}
    </label>
    {children}
  </div>
);

export const ContactSection = () => {
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    if (!form.full_name.trim()) return "Please enter your name.";
    if (!/^\S+@\S+\.\S+$/.test(form.work_email)) return "Please enter a valid work email.";
    if (!form.company.trim()) return "Please enter your company.";
    if (!form.role) return "Please select your role.";
    if (!form.need) return "Please select what best describes your need.";
    if (!form.project_stage) return "Please select your project stage.";
    if (!form.budget) return "Please select a budget range.";
    if (form.main_challenge.trim().length < 10)
      return "Please describe your main challenge (at least 10 characters).";
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSuccess(true);
      setForm(emptyForm);
      toast.success("Thanks — we've received your message. We'll be in touch shortly.");
    } catch (e2) {
      console.error(e2);
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid={TID.contactSection}
      className="relative bg-[#050505] py-24 md:py-32 border-t border-white/10"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20">
          {/* Left: pitch */}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-px w-8 bg-[#FF5F1F]" />
                <span className="font-mono text-[10px] md:text-xs uppercase tracking-[0.25em] text-[#FF5F1F]">
                  Start With the Challenge
                </span>
              </div>
              <h2 className="font-headings text-4xl md:text-5xl lg:text-6xl font-black tracking-tight leading-[1.02] text-white">
                Have a Product Challenge <em className="text-[#FF5F1F] not-italic">Worth Solving?</em>
              </h2>
              <p className="mt-8 text-base md:text-lg text-zinc-400 leading-relaxed max-w-md">
                Whether you are adding AI to an existing product, modernizing a complex platform,
                building a new SaaS business, or accelerating an ambitious roadmap — we can help
                you identify the right engineering path.
              </p>

              <div className="mt-10 pt-8 border-t border-white/10 space-y-4 text-sm text-zinc-500">
                <div className="flex items-start gap-3">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-[#FF5F1F] pt-1">
                    01
                  </span>
                  <span>No generic sales pitch — a focused engineering conversation.</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-[#FF5F1F] pt-1">
                    02
                  </span>
                  <span>Response from an engineer, not a scripted BDR.</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-[#FF5F1F] pt-1">
                    03
                  </span>
                  <span>Confidential by default. NDA on request.</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.1 }}
              transition={{ duration: 0.7 }}
              className="border border-white/10 bg-[#0A0A0A] p-6 md:p-10 relative"
            >
              <div className="absolute -top-3 left-6 md:left-10 bg-[#0A0A0A] px-3 font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF5F1F]">
                Tell Us About Your Challenge
              </div>

              {success ? (
                <div className="py-16 flex flex-col items-center text-center gap-6">
                  <div className="w-16 h-16 border border-[#FF5F1F] flex items-center justify-center">
                    <CheckCircle2 className="w-8 h-8 text-[#FF5F1F]" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-headings text-3xl md:text-4xl font-black tracking-tight text-white">
                    Message received.
                  </h3>
                  <p className="text-zinc-400 max-w-md">
                    Thanks for reaching out. An engineer from our team will get back to you shortly
                    — usually within one business day.
                  </p>
                  <button
                    onClick={() => setSuccess(false)}
                    className="font-mono text-xs uppercase tracking-[0.15em] text-[#FF5F1F] hover:underline"
                  >
                    Send another message →
                  </button>
                </div>
              ) : (
                <form
                  data-testid={TID.contactForm}
                  onSubmit={submit}
                  className="flex flex-col gap-6 md:gap-8"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                    <Field label="Full Name" required>
                      <input
                        data-testid={TID.contactInputName}
                        type="text"
                        value={form.full_name}
                        onChange={update("full_name")}
                        placeholder="Jane Doe"
                        className={inputCls}
                      />
                    </Field>
                    <Field label="Work Email" required>
                      <input
                        data-testid={TID.contactInputEmail}
                        type="email"
                        value={form.work_email}
                        onChange={update("work_email")}
                        placeholder="jane@company.com"
                        className={inputCls}
                      />
                    </Field>
                    <Field label="Company" required>
                      <input
                        data-testid={TID.contactInputCompany}
                        type="text"
                        value={form.company}
                        onChange={update("company")}
                        placeholder="Acme Inc."
                        className={inputCls}
                      />
                    </Field>
                    <Field label="Company Website">
                      <input
                        data-testid={TID.contactInputWebsite}
                        type="text"
                        value={form.website}
                        onChange={update("website")}
                        placeholder="https://acme.com"
                        className={inputCls}
                      />
                    </Field>
                    <Field label="Your Role" required>
                      <select
                        data-testid={TID.contactSelectRole}
                        value={form.role}
                        onChange={update("role")}
                        className={`${inputCls} appearance-none cursor-pointer`}
                      >
                        <option value="" className="bg-[#0A0A0A]">
                          Select role
                        </option>
                        {ROLES.map((r) => (
                          <option key={r} value={r} className="bg-[#0A0A0A]">
                            {r}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="What Best Describes Your Need?" required>
                      <select
                        data-testid={TID.contactSelectNeed}
                        value={form.need}
                        onChange={update("need")}
                        className={`${inputCls} appearance-none cursor-pointer`}
                      >
                        <option value="" className="bg-[#0A0A0A]">
                          Select need
                        </option>
                        {NEEDS.map((n) => (
                          <option key={n} value={n} className="bg-[#0A0A0A]">
                            {n}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Project Stage" required>
                      <select
                        data-testid={TID.contactSelectStage}
                        value={form.project_stage}
                        onChange={update("project_stage")}
                        className={`${inputCls} appearance-none cursor-pointer`}
                      >
                        <option value="" className="bg-[#0A0A0A]">
                          Select stage
                        </option>
                        {STAGES.map((s) => (
                          <option key={s} value={s} className="bg-[#0A0A0A]">
                            {s}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Budget Range" required>
                      <select
                        data-testid={TID.contactSelectBudget}
                        value={form.budget}
                        onChange={update("budget")}
                        className={`${inputCls} appearance-none cursor-pointer`}
                      >
                        <option value="" className="bg-[#0A0A0A]">
                          Select budget
                        </option>
                        {BUDGETS.map((b) => (
                          <option key={b} value={b} className="bg-[#0A0A0A]">
                            {b}
                          </option>
                        ))}
                      </select>
                    </Field>
                  </div>

                  <Field label="Main Challenge" required>
                    <textarea
                      data-testid={TID.contactTextareaChallenge}
                      value={form.main_challenge}
                      onChange={update("main_challenge")}
                      rows={5}
                      placeholder="Tell us what you are building, what is currently blocking progress, and what outcome you want to achieve."
                      className={`${inputCls} resize-none min-h-[120px]`}
                    />
                  </Field>

                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pt-4 border-t border-white/5">
                    <p className="text-xs text-zinc-500 max-w-sm leading-relaxed">
                      We use the information you provide only to understand your inquiry and
                      respond appropriately.
                    </p>
                    <button
                      data-testid={TID.contactSubmit}
                      type="submit"
                      disabled={loading}
                      className="group inline-flex items-center justify-center gap-2 bg-[#FF5F1F] hover:bg-[#E04F15] disabled:opacity-60 disabled:cursor-not-allowed text-black font-bold px-8 py-4 text-sm md:text-base transition-all"
                    >
                      {loading ? "Sending…" : "Discuss My Challenge"}
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </button>
                  </div>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
